import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Cookie } from "lucide-react";
import { LegalFooter } from "./privacy-policy";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LAST_UPDATED = "24 June 2025";

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href={`${BASE}/`}>
            <span className="font-bold text-lg text-primary cursor-pointer">RetailPulse</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href={`${BASE}/login`}><Button variant="ghost" size="sm">Sign in</Button></Link>
            <Link href={`${BASE}/signup`}><Button size="sm">Get started free</Button></Link>
          </div>
        </div>
      </header>

      <section className="border-b bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
            <Cookie className="w-3 h-3 mr-1" /> Legal
          </Badge>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Cookie Policy</h1>
          <p className="text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <div className="space-y-10 text-sm leading-relaxed text-foreground">

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">1. What are cookies?</h2>
            <p className="text-muted-foreground">
              Cookies are small text files placed on your device when you visit a website. They help the site remember your preferences, keep you logged in, and understand how you use the service. RetailPulse uses cookies and similar technologies (such as local storage) to operate the platform.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">2. Cookies we use</h2>

            <div className="overflow-auto rounded-xl border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium">Cookie / Key</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Purpose</th>
                    <th className="text-left p-4 font-medium">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["token", "Essential", "Stores your authentication session (httpOnly, Secure). Required to keep you logged in.", "7 days"],
                    ["csrf-token", "Essential", "Protects against cross-site request forgery attacks.", "Session"],
                    ["theme", "Functional", "Remembers your light/dark mode preference.", "1 year"],
                    ["onboarding_seen", "Functional", "Tracks whether you have completed the onboarding checklist.", "Persistent"],
                    ["_analytics", "Analytics", "Anonymous usage analytics to help us improve the product (aggregated only, no personal data).", "30 days"],
                  ].map(([name, type, purpose, duration]) => (
                    <tr key={name} className="border-b hover:bg-muted/30">
                      <td className="p-4 font-mono text-xs font-medium">{name}</td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          type === "Essential" ? "bg-blue-100 text-blue-800" :
                          type === "Functional" ? "bg-emerald-100 text-emerald-800" :
                          "bg-amber-100 text-amber-800"
                        }`}>{type}</span>
                      </td>
                      <td className="p-4 text-muted-foreground">{purpose}</td>
                      <td className="p-4 text-muted-foreground whitespace-nowrap">{duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">3. Essential cookies</h2>
            <p className="text-muted-foreground">
              The <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">token</code> cookie is strictly necessary for the platform to function. It is an httpOnly, Secure, SameSite cookie that holds your encrypted session. Without it, you cannot remain logged in. You cannot opt out of this cookie while using RetailPulse.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">4. Third-party cookies</h2>
            <p className="text-muted-foreground">
              RetailPulse does not currently embed third-party advertising or tracking scripts. Paystack (our payment processor) may set its own cookies when you interact with the subscription payment flow — these are governed by <a href="https://paystack.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">Paystack's Privacy Policy</a>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">5. Managing cookies</h2>
            <p className="text-muted-foreground">
              You can control cookies through your browser settings. Most browsers allow you to block or delete cookies. However, blocking essential cookies (particularly <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">token</code>) will prevent you from staying logged in to RetailPulse.
            </p>
            <p className="text-muted-foreground">
              Browser-specific instructions:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-primary underline">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary underline">Apple Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary underline">Microsoft Edge</a></li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">6. Updates to this policy</h2>
            <p className="text-muted-foreground">
              We may update this Cookie Policy when we add new features or third-party services. The "Last updated" date at the top reflects the most recent revision.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">7. Contact</h2>
            <p className="text-muted-foreground">
              Questions?{" "}
              <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a>
              {" "}or{" "}
              <a href="https://wa.me/2349040460813" target="_blank" rel="noopener noreferrer" className="text-primary underline">WhatsApp</a>.
            </p>
          </section>

        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
